import { Process, System } from '@gotti/core';
import { CLIENT_SYSTEM_PROTO } from '@gotti/client';
import { ServerProcess } from '@gotti/server';
import type { AreaServer } from '@gotti/server';
export * from './types.js';
export * from './input-simulator.js';

// Re-export shared types so consumers don't need a second import
export type {
  TestHarness,
  ServerToClientRouter,
  ServerToClientRequester,
  ClientToServerRequester,
} from './harness-core.js';

import {
  buildTestHarness,
  createAreaServerStub,
  setupSystemPair as _setupSystemPair,
  type TestHarness,
} from './harness-core.js';

// ── Node createTestHarness ───────────────────────────────────────────────────
// Uses the real ServerProcess from @gotti/server.

export function createTestHarness(clientCount = 1): TestHarness {
  const n = Math.max(1, clientCount);
  const serverProc = new ServerProcess();
  // N real clients sharing one server — ids match the live runtime so a
  // server's onClientJoined/onClientMessage sees the same `player-1..N` ids.
  const clientIds = Array.from({ length: n }, (_, i) => `player-${i + 1}`);
  const clientProcs = clientIds.map(() => new TestProcess());

  // Placeholder refs that buildTestHarness will populate via closure
  let harness: TestHarness;

  const areaServerStub = createAreaServerStub(
    () => harness.forwardToClient,
    () => harness.forwardRequestToClient,
  );

  serverProc.initialize(areaServerStub as unknown as AreaServer);

  // One stub connector per client (tagging its client→server dispatches with
  // that client's id) so client systems' dispatchToServer / requestArea / etc.
  // (installed via TestProcess.decorate copying CLIENT_SYSTEM_PROTO) bridge
  // into the harness's interception tables. See harness.browser.ts for the
  // protocol decoding rationale — same shape, both files mirror it.
  clientProcs.forEach((clientProc, i) => {
    const clientId = clientIds[i];
    clientProc.initialize({
      send: (args: any[]) => {
        if (!Array.isArray(args) || args.length < 4) return;
        const type = args[1];
        const data = args[2];
        const to = args[args.length - 1];
        const areaId = args.length === 5 ? args[3] : undefined;
        harness.forwardToServer({ type, data, to, areaId, clientId });
      },
      sendRaw: () => {},
      requestServer: (msg: any) => harness.forwardRequestToServer(msg),
    });
  });

  harness = buildTestHarness(serverProc, clientProcs, clientIds);

  // Auto-start is LAZY — see harness.browser.ts for the rationale.
  // start() fires on the first tick(), which means systems added in
  // beforeEach get their onStart() called naturally when the test
  // drives the first tick.

  return harness;
}

// ── Convenience wrappers ─────────────────────────────────────────────────────

export function setupSystemPair<S, C>(
  opts: { server: S | (new () => S); client: C | (new () => C) }
): {
  harness: TestHarness;
  // Player ids are plain strings throughout the framework; the harness API
  // shapes follow that rather than carrying any legacy union.
  server: S & {
    $api: { getPlayer: (playerId: string) => { id: string } | undefined };
    joinedPlayers: unknown[];
    onClientJoined?: (data: any) => void;
    onClientLeft?: (id: string, options?: any) => void;
  };
  client: C & { $api: { getPlayer: (playerId: string) => { id: string } | undefined } };
} {
  const { harness, server, client } = _setupSystemPair(createTestHarness, opts);
  return { harness, server: server as any, client: client as any };
}

// Convenience: wire server/client PlayerManager pair without subclassing
export function wirePlayerManagerRoute(
  harness: TestHarness,
  params: {
    to: string | number;
    getClientPM: () => any;
  }
) {
  const { to, getClientPM } = params;
  const PLAYER_MANAGER_EVENT_ADD = '__pm:add';
  const PLAYER_MANAGER_EVENT_REMOVE = '__pm:remove';

  harness.registerRoute(to, (msg) => {
    const clientPm = getClientPM();
    if (!clientPm) return;
    const { type, data } = msg;
    if (type === PLAYER_MANAGER_EVENT_ADD) {
      const d = data as Record<string, unknown>;
      const id = (typeof d.id === 'string' || typeof d.id === 'number') ? d.id : undefined;
      const opts = d.options as unknown;
      if (!id) return;
      if (clientPm.playerMap[String(id)]) return;
      const player = clientPm.options.playerFactory(opts);
      clientPm.addPlayer(player, opts);
    } else if (type === PLAYER_MANAGER_EVENT_REMOVE) {
      const d = data as Record<string, unknown>;
      const id = (typeof d.id === 'string' || typeof d.id === 'number') ? d.id : undefined;
      const opts = d.options as unknown;
      if (!id) return;
      if (!clientPm.playerMap[String(id)]) return;
      clientPm.removePlayer(String(id), opts);
    }
  });
}

// Mirrors packages/client/src/ClientProcess.ts closely enough for in-process
// integration tests. See the long comment in harness.browser.ts for the
// design rationale — this Node-side class is a copy of that one.

const TEST_CLIENT_CONNECTOR_METHODS = ['send', 'sendRaw', 'requestServer'] as const;
type TestClientConnector = { [K in (typeof TEST_CLIENT_CONNECTOR_METHODS)[number]]: (...args: any[]) => any };

// Seed `$areaData` with the single area the harness emulates (id matches
// `createAreaServerStub`'s 'test-area-1'). With exactly one entry,
// `proto.requestArea(...)` auto-resolves `areaId` so tests can call
// `requestArea({ to, type, data })` without knowing the magic id.
const DEFAULT_TEST_AREA_DATA: Readonly<Record<string, unknown>> = Object.freeze({
  'test-area-1': Object.freeze({}),
});

export class TestProcess extends Process {
  private _client: any = {};
  private _connector: TestClientConnector | null = null;

  public initialize(connector: TestClientConnector): void {
    this._connector = connector;
  }

  public decorate(system: System): System {
    if ((system as any).__is_client) return system;
    for (const key of Object.getOwnPropertyNames(CLIENT_SYSTEM_PROTO)) {
      if (key in system) continue;
      const desc = Object.getOwnPropertyDescriptor(CLIENT_SYSTEM_PROTO, key);
      if (desc) Object.defineProperty(system, key, desc);
    }
    Object.defineProperty(system, '__is_client', {
      value: true, writable: false, configurable: false, enumerable: false,
    });
    if (!('requestCbs' in system)) {
      Object.defineProperty(system, 'requestCbs', {
        value: {}, writable: true, configurable: true, enumerable: true,
      });
    }
    if (!('states' in system)) {
      Object.defineProperty(system, 'states', {
        value: {}, writable: true, configurable: true, enumerable: true,
      });
    }
    return system;
  }

  public onAddedSystem(system: System): void {
    if (!this._connector) return; // pre-initialize add: caller wires later
    Object.defineProperty(system, '$areaData', {
      get: () => DEFAULT_TEST_AREA_DATA,
      set: () => { throw new Error('Can not set $areaData on system'); },
      configurable: true,
    });
    (system as any).initialize?.(this._client, this._connector);
    (system as any).isNetworked = false;
  }

  protected onStartLoop(): void { this.started = true; }
  protected onStopLoop(): void { this.started = false; }
}
